/**
 * "Ask Amoreg about this": the opening text of a new chat about one iManage
 * document. The chat gets the document two ways — the opened version as an
 * attachment, and its iManage IDs, so the agent's own iManage tools (which run
 * with the person's own grant) can read it or look around it.
 *
 * @module components/apps/imanage/imanageAsk
 */
import type { IManageEntry } from "@amoreg/contracts/imanage";

import { clientMatter, downloadName, fileKind, fileKindLabel } from "./imanageFormat";

export interface AskContext {
  readonly library: string;
  readonly workspace?: Pick<IManageEntry, "name" | "client" | "matter"> | null;
  readonly folder?: Pick<IManageEntry, "name"> | null;
  /** True when the opened version's file is staged as an attachment. */
  readonly attached: boolean;
}

export function askAboutDocumentPrompt(entry: IManageEntry, context: AskContext): string {
  const where = context.workspace
    ? ` in ${[clientMatter(context.workspace), context.workspace.name].filter(Boolean).join(" ")}${
        context.folder ? ` › ${context.folder.name}` : ""
      }`
    : "";
  const version = entry.version !== null ? `, version ${entry.version}` : "";
  const lines = [
    `About the iManage ${entry.email ? "email" : "document"} “${entry.name}”${where} (${fileKindLabel(entry)}${version}; iManage ID ${entry.id}, library ${context.library}).`,
  ];
  if (context.attached) lines.push(`This version is attached as ${downloadName(entry)}.`);
  if (fileKind(entry) === "word") {
    lines.push(
      `You can also read this version with imanage_read_document (libraryId ${context.library}, documentId ${entry.id}, exactVersion: true).`,
    );
    const base = entry.id.replace(/\.\d+$/, "");
    if (entry.version !== null && entry.version > 1 && base !== entry.id)
      lines.push(
        `Earlier versions are ${base}.1 to ${base}.${entry.version - 1}.`,
      );
  }
  return `${lines.join(" ")}\n\n`;
}

const MIME_TYPES: Readonly<Record<string, string>> = {
  docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  doc: "application/msword",
  pdf: "application/pdf",
  eml: "message/rfc822",
  msg: "application/vnd.ms-outlook",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pptx: "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  txt: "text/plain",
};

/** The MIME type a downloaded iManage file is declared with. */
export const mimeTypeFor = (extension: string | undefined) =>
  (extension && MIME_TYPES[extension.toLowerCase()]) || "application/octet-stream";
